import {
  AccountTreeOutlined as AccountTreeOutlinedIcon,
  AdminPanelSettingsOutlined as AdminPanelSettingsOutlinedIcon,
  AssessmentOutlined as AssessmentOutlinedIcon,
  GppGoodOutlined as GppGoodOutlinedIcon,
  HomeRepairServiceOutlined as HomeRepairServiceOutlinedIcon,
  PrivacyTipOutlined as PrivacyTipOutlinedIcon,
  TaskAltOutlined as TaskAltOutlinedIcon,
} from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import {
  getSupervisorAdministrationPageConfig,
  supervisorAdministrationPageConfigs,
  supervisorAdministrationPageKeys,
} from '~/modules/supervisor-administration/config.ts';
import type { TSupervisorAdministrationPageKey } from '~/modules/supervisor-administration/types.ts';
import type { IGuaranteesNavigationConfigItem } from './types.ts';

export const MANAGER_MENU_LABEL = 'Меню';

export const MANAGER_LAYOUT_PATHS = {
  home: '/services',
  managerSignIn: '/manager/sign-in',
  tasks: '/tasks',
  guaranteesRoot: '/guarantees',
  guaranteesStart: '/guarantees/get-started',
  guaranteesAdministratorStart: '/guarantees/administrator/get-started',
  guaranteesCreateStart: '/guarantees/administrator/get-started?create=true',
  guaranteesClientPagePattern: /^\/guarantees\/(?:administrator\/)?contragents\/[^/]+\/my$/,
  guaranteesApplicationPagePattern:
    /^\/guarantees\/(?:application\/[^/]+|administrator\/application\/[^/]+(?:\/step[23])?)$/,
  covenantsInstruction: '/covenants/uakdo-instruction',
  covenantsRoot: '/covenants',
} as const;

const supervisorAdministrationVisibilityKeys = {
  usapok: 'showUsapokAdministration',
  umvn: 'showUmvnAdministration',
  uo: 'showUoAdministration',
  udbtf: 'showUdbtfAdministration',
} as const satisfies Record<
  TSupervisorAdministrationPageKey,
  IGuaranteesNavigationConfigItem['visibilityKey']
>;

const getNavigationSegment = (path: string) => path.replace(/^\//, '');

export const managerTopLevelNavigationConfig = [
  {
    segment: 'services',
    pattern: 'services',
    title: 'Послуги',
    visibilityKey: 'showServices',
  },
  {
    segment: 'application-reports',
    pattern: 'application-reports',
    title: 'Ідентифікація',
    visibilityKey: 'showReports',
  },
  {
    segment: 'personal-data-table',
    pattern: 'personal-data-table',
    title: 'Обробка даних',
    visibilityKey: 'showPersonalData',
  },
  {
    segment: 'covenants/manager/segments',
    title: 'УАКДО',
    visibilityKey: 'showAdministration',
  },
  ...supervisorAdministrationPageKeys.map((pageKey) => ({
    segment: getNavigationSegment(getSupervisorAdministrationPageConfig(pageKey).path),
    title: getSupervisorAdministrationPageConfig(pageKey).navigationTitle,
    visibilityKey: supervisorAdministrationVisibilityKeys[pageKey],
  })),
] as const satisfies readonly IGuaranteesNavigationConfigItem[];

export const managerCovenantsNavigationConfig = [
  {
    segment: 'manager/applications',
    title: 'Заявки',
    visibilityKey: 'showApplications',
  },
  {
    segment: 'operator/get-started',
    title: 'Оператор кол-центру',
    visibilityKey: 'showCallCenterOperator',
  },
  {
    segment: 'covenants-branch-manager/get-started',
    title: 'Менеджер філії',
    visibilityKey: 'showBranchManager',
  },
  {
    segment: 'uakdo-administrator/get-started',
    title: 'Зайти як клієнт',
    visibilityKey: 'showEnterAsClient',
  },
  {
    segment: 'uakdo-instruction',
    title: 'Інструкція УАКДО',
    visibilityKey: 'showUakdoInstruction',
  },
  {
    segment: 'manager/email-blacklist',
    title: 'Чорний список розсилки',
    visibilityKey: 'showEmailBlackList',
  },
] as const satisfies readonly IGuaranteesNavigationConfigItem[];

export const managerGuaranteesNavigationConfig = [
  {
    segment: 'administrator/get-started?create=true',
    title: 'Створити заявку',
    visibilityKey: 'showGuarantees',
  },
] as const satisfies readonly IGuaranteesNavigationConfigItem[];

export const managerSecondaryNavigationConfig = [
  {
    segment: 'tasks',
    pattern: 'tasks',
    title: 'Мої Завдання',
    visibilityKey: 'showMyTasks',
  },
] as const satisfies readonly IGuaranteesNavigationConfigItem[];

export const managerCompactNavigationIcons: Record<string, SvgIconComponent> = {
  services: HomeRepairServiceOutlinedIcon,
  'application-reports': AssessmentOutlinedIcon,
  'personal-data-table': PrivacyTipOutlinedIcon,
  'covenants/manager/segments': AdminPanelSettingsOutlinedIcon,
  [getNavigationSegment(supervisorAdministrationPageConfigs.usapok.path)]:
    AdminPanelSettingsOutlinedIcon,
  [getNavigationSegment(supervisorAdministrationPageConfigs.umvn.path)]:
    AdminPanelSettingsOutlinedIcon,
  [getNavigationSegment(supervisorAdministrationPageConfigs.uo.path)]:
    AdminPanelSettingsOutlinedIcon,
  [getNavigationSegment(supervisorAdministrationPageConfigs.udbtf.path)]:
    AdminPanelSettingsOutlinedIcon,
  covenants: AccountTreeOutlinedIcon,
  guarantees: GppGoodOutlinedIcon,
  tasks: TaskAltOutlinedIcon,
};

export const GUARANTEES_MENU_LABEL = MANAGER_MENU_LABEL;
export const GUARANTEES_LAYOUT_PATHS = MANAGER_LAYOUT_PATHS;
export const guaranteesTopLevelNavigationConfig = managerTopLevelNavigationConfig;
export const guaranteesCovenantsNavigationConfig = managerCovenantsNavigationConfig;
export const guaranteesNavigationConfig = managerGuaranteesNavigationConfig;
export const guaranteesSecondaryNavigationConfig = managerSecondaryNavigationConfig;
export const guaranteesCompactNavigationIcons = managerCompactNavigationIcons;
